/**
 * What the helper can answer when a child asks something during the Tongue Lab: a fixed list of
 * topics, each with an approved answer (docs/design/articulation-spec.md for the r facts). The
 * language model only picks a topic (`chooseTopic`); it never writes what the child hears, so a
 * wrong pick gives a wrong but safe answer, and `none` asks the child to try other words or a
 * grown-up.
 */
export interface Topic {
  id: string;
  /** What the topic is about, as the language model and the keyword fallback read it. */
  about: string;
  /** Words that point to this topic (lowercase, whole words or short phrases). */
  keys: readonly string[];
  /** The approved answer, read or spoken to the child. */
  answer: string;
}

export const TOPICS: readonly Topic[] = [
  {
    id: 'where',
    about: 'where the tongue goes for the r sound',
    keys: ['where', 'tongue go', 'put my tongue', 'position', 'place'],
    answer:
      'For a strong r, the middle of your tongue lifts up close to the roof of your mouth. The sides press on your back teeth, and the tip stays low.',
  },
  {
    id: 'touch',
    about: 'whether the tongue touches the roof of the mouth',
    keys: ['touch', 'roof', 'top of my mouth', 'hit'],
    answer:
      'Close, but not touching! If your tongue touches the roof, the sound stops. Leave a tiny gap so the air can get through.',
  },
  {
    id: 'tip',
    about: 'whether the tongue tip goes up or down',
    keys: ['tip', 'front of my tongue', 'up or down', 'curl'],
    answer:
      'For this r, the tip stays low, behind your bottom teeth. Some people curl the tip up instead, and that works too, but we practise the low-tip way here.',
  },
  {
    id: 'lips',
    about: 'what the lips do',
    keys: ['lips', 'lip', 'mouth round', 'smile', 'kiss'],
    answer:
      'Your lips round just a little, not a big kiss. If they push far out, it starts to sound like a w.',
  },
  {
    id: 'w',
    about: 'the difference between r and w',
    keys: ['w', 'double u', 'wabbit', 'difference', 'same'],
    answer:
      'For a w, the back of your tongue lifts and your lips round a lot, like "oo". For a strong r, the middle of your tongue lifts and your lips stay almost relaxed.',
  },
  {
    id: 'hard',
    about: 'why the r sound is hard to learn',
    keys: ['hard', 'difficult', 'why can', 'tricky', 'last'],
    answer:
      'The r is one of the last sounds children learn, because your tongue does three things at once. Lots of kids practise it. It gets easier each time you try.',
  },
  {
    id: 'already',
    about: 'the child says they can already say r',
    keys: ['already', 'i can say', 'i know how', 'easy', 'can do it'],
    answer:
      'Great, then this will go fast! Even people who can say r practise it to make it stronger. Show me your best r and I will listen.',
  },
  {
    id: 'right',
    about: 'how to know if the r was right',
    keys: ['right', 'correct', 'good', 'did i', 'how do i know'],
    answer:
      'A strong r sounds like a growl: "rrr". I listen to each try and tell you what I heard. When it sounds like "w", the middle of your tongue needs to lift more.',
  },
  {
    id: 'why',
    about: 'why practise or why this lesson',
    keys: ['why do', 'why are we', 'point', 'boring', 'have to'],
    answer:
      'Practice moves your tongue the new way again and again, until it does it on its own. A few good tries each day work better than lots at once.',
  },
  {
    id: 'model',
    about: 'what the picture or model on the screen shows',
    keys: ['what is this', 'picture', 'pink', 'blue dot', 'screen', 'that thing'],
    answer:
      'That is a mouth seen through the cheek. The pink shape is the tongue, and the teeth help you see where it is. Watch how the tongue moves.',
  },
];

/** The answer when no topic fits. */
export const NO_TOPIC =
  "I don't know that one yet. Try asking another way, or ask a grown-up. I can tell you where the tongue goes for r!";
